import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, ArrowLeft, InfoIcon, AlertCircle } from "lucide-react";
import { createLogger } from "@/lib/utils/logger";

// Create a logger for tracking password reset issues
const logger = createLogger('auth', 'password-reset');

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const { toast } = useToast();

  // Handle cooldown countdown
  useEffect(() => {
    let timer: number | undefined;
    
    if (cooldownActive && cooldownTime > 0) {
      timer = window.setInterval(() => {
        setCooldownTime((prevTime) => {
          const newTime = prevTime - 1;
          if (newTime <= 0) {
            setCooldownActive(false);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownActive, cooldownTime]);

  const startCooldown = (seconds = 60) => {
    setCooldownActive(true);
    setCooldownTime(seconds);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (cooldownActive) {
      setError(`Please wait ${cooldownTime} seconds before requesting another password reset email`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Add timeout to prevent hanging requests - increased to 30 seconds
      const resetPromise = supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Password reset service timeout (30s). Please try again later.")), 30000);
      });
      
      // Race the promises
      const result = await Promise.race([
        resetPromise,
        timeoutPromise
      ]) as { error: Error | null };

      if (result.error) {
        if (result.error.message.includes("rate limit") || result.error.message.includes("429")) {
          throw new Error("You've requested too many password reset emails. Please wait a few minutes before trying again.");
        }
        throw result.error;
      }
      
      // Try to log successful request, but don't fail if logging fails
      try {
        logger.info(`Password reset email sent to ${email.substring(0, 3)}...`);
      } catch (logError) {
        console.info("Non-critical error: Unable to log successful password reset request");
      }
      
      setSuccessMessage("Password reset instructions sent to your email");
      
      // Start cooldown to prevent rapid requests
      startCooldown();
      // Reset retry count on success
      setRetryCount(0);
      
    } catch (error) {
      const err = error as Error;
      // Increment retry count
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      console.error("Password reset error:", err);
      
      // Try to log error, but don't fail if logging fails
      try {
        logger.error(`Password reset error for ${email.substring(0, 3)}...: ${err.message}`);
      } catch (logError) {
        console.warn("Non-critical error: Unable to log password reset error");
      }
      
      // Customize message based on the error
      let userMessage = err.message;
      let cooldownPeriod = 60; // Default cooldown
      
      if (err.message.includes("timed out") || err.message.includes("deadline exceeded") || 
          err.message.includes("busy") || err.message.includes("504")) {
        
        if (newRetryCount >= 3) {
          userMessage = "Our password reset service is experiencing high demand. Please try again later or contact support if this persists.";
          cooldownPeriod = 300; // 5 minutes for repeated timeouts
        } else {
          userMessage = "The password reset service is currently busy. Please try again in a few minutes.";
          cooldownPeriod = 120; // 2 minutes for first timeouts
        }
      } else if (err.message.includes("rate limit") || err.message.includes("429")) {
        userMessage = "You've requested too many password reset emails. Please wait before trying again.";
        cooldownPeriod = 300; // 5 minutes for rate limits
      } else if (err.message.includes("network") || err.message.includes("connection")) {
        userMessage = "Network error detected. Please check your internet connection and try again.";
      }
      
      setError(userMessage);
      
      toast({
        variant: "destructive",
        title: "Password Reset Failed",
        description: userMessage,
      });
      
      // Set appropriate cooldown
      startCooldown(cooldownPeriod);
      
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
      <div className="w-full max-w-md px-4">
        <Card className="backdrop-blur-sm border-primary/20">
          <CardHeader className="space-y-1">
            <div className="flex flex-col items-center mb-4">
              <img 
                src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
                alt="NWSISD Logo"
                className="h-24 w-auto mb-4"
              />
            </div>
            <CardTitle className="text-2xl text-center font-title text-primary">Reset Password</CardTitle>
            <CardDescription className="text-center font-sans">
              Enter your email to receive password reset instructions
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  <AlertDescription className="font-serif">{error}</AlertDescription>
                </Alert>
              )}
              {successMessage && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="font-serif text-green-800">{successMessage}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="font-serif">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading || cooldownActive}
                  className="font-sans"
                />
              </div>
              
              {cooldownActive && (
                <Alert className="bg-blue-50 border-blue-200">
                  <InfoIcon className="h-4 w-4 text-blue-500 mr-2" />
                  <AlertDescription className="font-serif text-blue-800">
                    Please wait {cooldownTime} seconds before requesting another email
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="text-sm text-muted-foreground font-serif">
                <p>If you don't receive the email:</p>
                <ul className="list-disc ml-5 mt-1">
                  <li>Check your spam or junk folder</li>
                  <li>Verify the email address is correct</li>
                  <li>Try again after a few minutes if needed</li>
                  {retryCount >= 2 && (
                    <li>Our system may be experiencing high demand. Consider trying at a less busy time.</li>
                  )}
                </ul>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || cooldownActive || !email}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span className="font-serif">Sending...</span>
                  </>
                ) : cooldownActive ? (
                  <span className="font-serif">Wait {cooldownTime}s to resend</span>
                ) : (
                  <span className="font-serif">Send Reset Instructions</span>
                )}
              </Button>
              <Link to="/auth/login" className="flex items-center justify-center text-sm text-primary hover:underline font-serif">
                <ArrowLeft className="mr-1 h-4 w-4" /> Back to Login
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
